<template>
  <div>
    <h4 class="mb-3 mb-4 pb-3 border-bottom d-flex">
      <span>{{ $t('order.hafciarnia.orderInHafciarnia') }}</span>
    </h4>

    <d-card class="mb-4">
      <d-card-body>
        <d-row>
          <d-col cols="12">
            <d-alert
              v-if="!embrioderyId"
              theme="warning"
              show>
              {{ $t('order.hafciarnia.noHafciarniaIdInfo')}}
            </d-alert>
            <a
              v-else
              :href="hafciarniaUrl + 'zamowienia/edycja/' + parseInt(embrioderyId, 10)"
              target="_blank"
              rel="noopener noreferrer">
              {{ $t('order.hafciarnia.orderInHafciarnia') }}
            </a>
            <p class="mb-0">
              <template v-if="syncDate">
                {{ $t('order.hafciarnia.syncDate') }} <strong>{{ syncDate }}</strong>
              </template>
              <template v-else>
                <span class="text-warning">{{ $t('order.hafciarnia.syncInProgress') }}</span>
              </template>
            </p>
          </d-col>
        </d-row>
      </d-card-body>
    </d-card>

    <d-card
      v-if="embrioderyLogs">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0" style="width: 200px">{{ $t('order.hafciarnia.embrioderyLogs.createdAt') }}</th>
            <th scope="col" class="border-0" style="width: 150px">{{ $t('order.hafciarnia.embrioderyLogs.source') }}</th>
            <th scope="col" class="border-0" style="width: calc(100% - 600px)">{{ $t('order.hafciarnia.embrioderyLogs.action') }}</th>
            <th scope="col" class="border-0" style="width: 250px;">{{ $t('order.hafciarnia.embrioderyLogs.status') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(logItem, index) of embrioderyLogs"
            :key="'embriodery-log-item-' + index">
            <td class="align-middle">
              {{ logItem.created_at }}
            </td>
            <td class="align-middle">
              <template v-if="logItem.source === 'ORDER'">
                {{ $t('order.hafciarnia.embrioderyLogs.sourceType.order') }}
              </template>
              <template v-if="logItem.source === 'PRODUCT'">
                {{ $t('order.hafciarnia.embrioderyLogs.sourceType.product') }}
              </template>
              <template v-if="logItem.source === 'SERVICE'">
                {{ $t('order.hafciarnia.embrioderyLogs.sourceType.service') }}
              </template>
            </td>
            <td class="align-middle">
              <template v-if="logItem.action === 'SYNCHRONIZE'">
                {{ $t('order.hafciarnia.embrioderyLogs.actionType.synchronize') }}
              </template>
              <template v-if="logItem.action === 'ADD_SAMPLE'">
                {{ $t('order.hafciarnia.embrioderyLogs.actionType.add_sample') }}
              </template>
              <template v-if="logItem.action === 'SYNCHRONIZE_PRODUCTION_ENDED'">
                {{ $t('order.hafciarnia.embrioderyLogs.actionType.synchronize_production_ended') }}
              </template>
              <template v-if="logItem.action === 'SYNCHRONIZE_FILES'">
                {{ $t('order.hafciarnia.embrioderyLogs.actionType.synchronize_files') }}
              </template>
              <template v-if="logItem.action === 'SYNCHRONIZE_EMBROIDERY_RETURN'">
                {{ $t('order.hafciarnia.embrioderyLogs.actionType.synchronize_embroidery_return') }}
              </template>
              <template v-if="logItem.action === 'ADD_STATUS'">
                {{ $t('order.hafciarnia.embrioderyLogs.actionType.add_status') }}
              </template>
            </td>
            <td class="align-middle">
              <template v-if="logItem.status === null">
                &mdash;
              </template>
              <d-badge
                v-else
                :theme="orderStatuses[logItem.status] ? orderStatuses[logItem.status].style : 'secondary'"
                :outline="orderStatuses[logItem.status] ? orderStatuses[logItem.status].outline : false">
                {{ orderStatuses[logItem.status] ? orderStatuses[logItem.status].label : '??' }}
              </d-badge>
            </td>
          </tr>
        </tbody>
      </table>
    </d-card>

    <d-card
      v-if="embrioderyReturns && embrioderyReturns.length"
      class="mt-4">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0" style="width: 250px;">{{ $t('order.hafciarnia.embrioderyReturns.createdAt') }}</th>
            <th scope="col" class="border-0" style="width: calc(100% - 600px);">{{ $t('order.hafciarnia.embrioderyReturns.products') }}</th>
            <th scope="col" class="border-0" style="width: 350px;">{{ $t('order.hafciarnia.embrioderyReturns.status') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(returnItem, index) of embrioderyReturns"
            :key="'embriodery-return-item-' + index">
            <td class="align-middle">
              {{ returnItem.created_at }}
            </td>
            <td class="align-middle">
              <div
                v-for="(product, subindex) of returnItem.products"
                :key="'embriodery-return-item-' + index + '-' + subindex">
                <small>{{ product.product_code }} - {{ product.quantity }} {{ $t('ui.piecesAbbr') }}</small>
              </div>
            </td>
            <td class="align-middle">
              <template v-if="returnItem.status === 'TO_RETURN'">
                {{ $t('order.hafciarnia.embrioderyReturns.statuses.toReturn') }}
              </template>
              <template v-if="returnItem.status === 'RETURNED'">
                {{ $t('order.hafciarnia.embrioderyReturns.statuses.returned') }}
              </template>
              <template v-if="returnItem.status === 'RECEIVED_IN_HPL'">
                {{ $t('order.hafciarnia.embrioderyReturns.statuses.receivedInHPL') }}
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </d-card>
  </div>
</template>

<script>
import AppConfig from '@/.env';
import OrderStatuses from './../../data/embroidery-order-statuses.js';

export default {
  name: 'order-hafciarnia',
  props: {
    embrioderyId: {
      required: true,
      type: Number
    },
    embrioderyLogs: {
      required: true
    },
    embrioderyReturns: {
      required: true
    },
    orderID: {
      required: true,
      type: Number
    },
    syncDate: {
      default: '',
      type: String
    }
  },
  computed: {
    hafciarniaUrl () {
      let environment = AppConfig.useConfig;
      return AppConfig.additionalConfig[environment].hafciarniaUrl;
    },
    orderStatuses () {
      return OrderStatuses;
    }
  }
}
</script>

